import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  FileSignature,
  Send,
  RefreshCw,
  Search,
  Plus,
  ArrowLeft,
  Users,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Clock,
  FileText,
  Mail,
  Bell,
  Eye,
  Download,
  MoreHorizontal,
  Trash2,
  Copy,
  Building2,
  User,
  Filter,
  Calendar,
  Loader2,
  X,
  ChevronRight,
  Upload,
  Link2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

// Document templates
const DOCUMENT_TEMPLATES = [
  { id: 'offer_letter', name: 'Offer Letter', icon: FileText, category: 'hiring' },
  { id: 'nda', name: 'Non-Disclosure Agreement', icon: FileSignature, category: 'legal' },
  { id: 'employment_agreement', name: 'Employment Agreement', icon: FileText, category: 'hiring' },
  { id: 'contractor_agreement', name: 'Contractor Agreement', icon: FileText, category: 'hiring' },
  { id: 'confidentiality', name: 'Confidentiality Agreement', icon: FileSignature, category: 'legal' },
  { id: 'background_check', name: 'Background Check Authorization', icon: FileText, category: 'compliance' },
  { id: 'custom', name: 'Custom Document', icon: Upload, category: 'custom' },
];

// Status configurations
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: FileText },
  pending: { label: 'Pending Signature', color: 'bg-amber-100 text-amber-700', icon: Clock },
  partially_signed: { label: 'Partially Signed', color: 'bg-blue-100 text-blue-700', icon: Users },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-700', icon: Clock },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: X },
};

// Signer role configurations
const SIGNER_ROLES = [
  { id: 'candidate', name: 'Candidate', icon: User },
  { id: 'hr', name: 'HR Manager', icon: Users },
  { id: 'employer', name: 'Employer/Company', icon: Building2 },
];

const RabbitSignIntegration = ({ onBack, candidateId = null, candidateName = null, candidateEmail = null }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0 });
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showNewDocument, setShowNewDocument] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);

  // New document form state
  const [newDocument, setNewDocument] = useState({
    name: '',
    template: '',
    documentUrl: '',
    signers: [],
    message: '',
    expiresIn: '7' // days
  });
  const [addingSigner, setAddingSigner] = useState(false);
  const [newSigner, setNewSigner] = useState({ name: '', email: '', role: 'candidate' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  // If candidateId is provided, pre-fill signer
  useEffect(() => {
    if (candidateId && candidateEmail && candidateName) {
      setNewDocument(prev => ({
        ...prev,
        signers: [{ name: candidateName, email: candidateEmail, role: 'candidate', order: 1 }]
      }));
    }
  }, [candidateId, candidateEmail, candidateName]);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/rabbitsign/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(response.data.connected);
      if (response.data.connected) {
        await fetchDocuments();
      }
    } catch (error) {
      console.error('Error checking RabbitSign connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/integrations/rabbitsign/documents`;
      if (candidateId) {
        url += `?candidate_id=${candidateId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
      setStats(response.data.stats || { total: 0, pending: 0, completed: 0 });
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
    }
  };

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/rabbitsign/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setConnected(true);
        toast.success('RabbitSign connected successfully');
        await fetchDocuments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/rabbitsign/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchDocuments();
      toast.success('Documents synced');
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const addSigner = () => {
    if (!newSigner.name || !newSigner.email) {
      toast.error('Please fill in signer name and email');
      return;
    }
    const order = newDocument.signers.length + 1;
    setNewDocument(prev => ({
      ...prev,
      signers: [...prev.signers, { ...newSigner, order }]
    }));
    setNewSigner({ name: '', email: '', role: 'candidate' });
    setAddingSigner(false);
  };

  const removeSigner = (index) => {
    setNewDocument(prev => ({
      ...prev,
      signers: prev.signers.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }))
    }));
  };

  const handleSendForSignature = async () => {
    if (!newDocument.name) {
      toast.error('Please enter a document name');
      return;
    }
    if (newDocument.signers.length === 0) {
      toast.error('Please add at least one signer');
      return;
    }
    if (!newDocument.template && !newDocument.documentUrl) {
      toast.error('Please select a template or provide a document URL');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: newDocument.name,
        template_id: newDocument.template || null,
        document_url: newDocument.documentUrl || null,
        signers: newDocument.signers,
        message: newDocument.message,
        expires_in_days: parseInt(newDocument.expiresIn),
        candidate_id: candidateId || null
      };

      const response = await axios.post(`${API}/api/integrations/rabbitsign/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Document sent for signature!');
        setShowNewDocument(false);
        setNewDocument({
          name: '',
          template: '',
          documentUrl: '',
          signers: candidateEmail ? [{ name: candidateName, email: candidateEmail, role: 'candidate', order: 1 }] : [],
          message: '',
          expiresIn: '7'
        });
        await fetchDocuments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send document');
    } finally {
      setSending(false);
    }
  };

  const handleSendReminder = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/rabbitsign/documents/${documentId}/remind`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reminder sent to pending signers');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleCancelDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/rabbitsign/documents/${documentId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document cancelled');
      await fetchDocuments();
    } catch (error) {
      toast.error('Failed to cancel document');
    }
  };

  const handleViewDocument = (document) => {
    if (document.view_url) {
      window.open(document.view_url, '_blank');
    } else {
      toast.info('Document preview not available');
    }
  };

  const handleDownloadDocument = (document) => {
    if (document.download_url) {
      window.open(document.download_url, '_blank');
    } else {
      toast.info('Download not available yet');
    }
  };

  const getFilteredDocuments = () => {
    let filtered = documents;
    
    if (filter !== 'all') {
      filtered = filtered.filter(doc => doc.status === filter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(query) ||
        doc.signers?.some(s => s.name.toLowerCase().includes(query) || s.email.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };

  const filteredDocuments = getFilteredDocuments();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Not connected state
  if (!connected) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        {onBack && (
          <Button variant="ghost" onClick={onBack} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Apps
          </Button>
        )}
        
        <Card className="border-2 border-dashed border-orange-200">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <FileSignature className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Connect RabbitSign</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Send documents for e-signature to candidates, HR, and employers. 
              Track signing status and get notified when documents are completed.
            </p>
            <div className="flex flex-col gap-3 items-center">
              <Button 
                onClick={handleConnect}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              >
                <Link2 className="h-4 w-4 mr-2" />
                Connect RabbitSign
              </Button>
              <p className="text-xs text-slate-400">
                Requires RabbitSign API key configured in Admin Settings
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="rabbitsign-integration">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <FileSignature className="h-5 w-5 text-white" />
              </div>
              RabbitSign
            </h1>
            <p className="text-slate-500 text-sm mt-1">E-Signature documents • Zero Storage</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleSync}
            disabled={syncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync
          </Button>
          <Button 
            onClick={() => setShowNewDocument(true)}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Document
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-200">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-200">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
                <p className="text-xs text-amber-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-200">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
                <p className="text-xs text-green-600">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-200">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.total - stats.completed - stats.pending}</p>
                <p className="text-xs text-blue-600">Other</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search documents or signers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'completed', 'declined'].map(status => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              {status === 'all' ? 'All' : STATUS_CONFIG[status]?.label || status}
            </Button>
          ))}
        </div>
      </div>

      {/* Documents List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Documents</span>
            <Badge variant="outline">{filteredDocuments.length} items</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileSignature className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No documents found</p>
              <p className="text-sm text-slate-400 mt-1">
                {filter !== 'all' ? 'Try changing the filter' : 'Send your first document for signature'}
              </p>
              <Button 
                onClick={() => setShowNewDocument(true)} 
                className="mt-4 bg-orange-500 hover:bg-orange-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Document
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {filteredDocuments.map((doc) => {
                  const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-orange-200 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                          <FileSignature className="h-6 w-6 text-orange-500" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">{doc.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-500">
                              {doc.signers?.length || 0} signer{doc.signers?.length !== 1 ? 's' : ''}
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="text-xs text-slate-500">
                              {doc.created_at ? format(new Date(doc.created_at), 'MMM d, yyyy') : 'Unknown date'}
                            </span>
                          </div>
                          {/* Signers preview */}
                          <div className="flex items-center gap-1 mt-2">
                            {doc.signers?.slice(0, 3).map((signer, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline" 
                                className={`text-[10px] ${signer.signed ? 'bg-green-50 text-green-700' : 'bg-slate-50'}`}
                              >
                                {signer.signed ? <CheckCircle className="h-2.5 w-2.5 mr-1" /> : <Clock className="h-2.5 w-2.5 mr-1" />}
                                {signer.name.split(' ')[0]}
                              </Badge>
                            ))}
                            {doc.signers?.length > 3 && (
                              <Badge variant="outline" className="text-[10px]">
                                +{doc.signers.length - 3}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDocument(doc)}>
                              <Eye className="h-4 w-4 mr-2" /> View Document
                            </DropdownMenuItem>
                            {doc.status === 'completed' && (
                              <DropdownMenuItem onClick={() => handleDownloadDocument(doc)}>
                                <Download className="h-4 w-4 mr-2" /> Download Signed
                              </DropdownMenuItem>
                            )}
                            {(doc.status === 'pending' || doc.status === 'partially_signed') && (
                              <>
                                <DropdownMenuItem onClick={() => handleSendReminder(doc.id)}>
                                  <Bell className="h-4 w-4 mr-2" /> Send Reminder
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleCancelDocument(doc.id)}
                                  className="text-red-600"
                                >
                                  <X className="h-4 w-4 mr-2" /> Cancel
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => {
                              navigator.clipboard.writeText(doc.view_url || '');
                              toast.success('Link copied');
                            }}>
                              <Copy className="h-4 w-4 mr-2" /> Copy Link
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* New Document Dialog */}
      <Dialog open={showNewDocument} onOpenChange={setShowNewDocument}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-orange-500" />
              Send Document for Signature
            </DialogTitle>
            <DialogDescription>
              Create a new signature request. Documents are stored in RabbitSign (Zero Storage).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Document Name */}
            <div>
              <Label>Document Name *</Label>
              <Input
                placeholder="e.g., Offer Letter - John Doe"
                value={newDocument.name}
                onChange={(e) => setNewDocument(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1.5"
              />
            </div>

            {/* Template Selection */}
            <div>
              <Label>Document Template</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {DOCUMENT_TEMPLATES.map(template => {
                  const TemplateIcon = template.icon;
                  const isSelected = newDocument.template === template.id;
                  return (
                    <button
                      key={template.id}
                      onClick={() => setNewDocument(prev => ({ 
                        ...prev, 
                        template: template.id,
                        documentUrl: template.id === 'custom' ? prev.documentUrl : ''
                      }))}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        isSelected 
                          ? 'border-orange-400 bg-orange-50 shadow-sm' 
                          : 'border-slate-200 hover:border-orange-200'
                      }`}
                    >
                      <TemplateIcon className={`h-4 w-4 ${isSelected ? 'text-orange-500' : 'text-slate-400'}`} />
                      <span className={`text-sm ${isSelected ? 'text-orange-700 font-medium' : 'text-slate-600'}`}>
                        {template.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Document URL */}
            {newDocument.template === 'custom' && (
              <div>
                <Label>Document URL (PDF)</Label>
                <Input
                  placeholder="https://drive.google.com/file/d/... or any public PDF URL"
                  value={newDocument.documentUrl}
                  onChange={(e) => setNewDocument(prev => ({ ...prev, documentUrl: e.target.value }))}
                  className="mt-1.5"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Paste a Google Drive, Dropbox, or any public PDF link
                </p>
              </div>
            )}

            {/* Signers */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Signers *</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setAddingSigner(true)}
                  className="text-orange-600 hover:text-orange-700"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Signer
                </Button>
              </div>
              
              {newDocument.signers.length === 0 ? (
                <div className="text-center py-6 border border-dashed rounded-lg">
                  <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No signers added yet</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => setAddingSigner(true)}
                  >
                    Add First Signer
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {newDocument.signers.map((signer, idx) => {
                    const roleConfig = SIGNER_ROLES.find(r => r.id === signer.role) || SIGNER_ROLES[0];
                    const RoleIcon = roleConfig.icon;
                    return (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-medium text-sm">
                            {signer.order}
                          </div>
                          <div>
                            <p className="font-medium text-slate-700 text-sm">{signer.name}</p>
                            <p className="text-xs text-slate-500">{signer.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <RoleIcon className="h-3 w-3 mr-1" />
                            {roleConfig.name}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeSigner(idx)}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Signer Form */}
              {addingSigner && (
                <div className="mt-3 p-4 border rounded-lg bg-orange-50/50 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input
                        placeholder="John Doe"
                        value={newSigner.name}
                        onChange={(e) => setNewSigner(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input
                        placeholder="john@example.com"
                        value={newSigner.email}
                        onChange={(e) => setNewSigner(prev => ({ ...prev, email: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Select 
                      value={newSigner.role} 
                      onValueChange={(v) => setNewSigner(prev => ({ ...prev, role: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SIGNER_ROLES.map(role => (
                          <SelectItem key={role.id} value={role.id}>
                            <span className="flex items-center gap-2">
                              <role.icon className="h-4 w-4" />
                              {role.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setAddingSigner(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={addSigner} className="bg-orange-500 hover:bg-orange-600">
                      Add Signer
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Message */}
            <div>
              <Label>Message to Signers (optional)</Label>
              <Textarea
                placeholder="Please review and sign the attached document..."
                value={newDocument.message}
                onChange={(e) => setNewDocument(prev => ({ ...prev, message: e.target.value }))}
                className="mt-1.5"
                rows={3}
              />
            </div>

            {/* Expiry */}
            <div>
              <Label>Expires In</Label>
              <Select 
                value={newDocument.expiresIn} 
                onValueChange={(v) => setNewDocument(prev => ({ ...prev, expiresIn: v }))}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDocument(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendForSignature}
              disabled={sending}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send for Signature
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zero Storage Notice */}
      <Card className="mt-6 bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
              <FileSignature className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-orange-900">Zero Storage Architecture</h3>
              <p className="text-sm text-orange-700 mt-1">
                Documents are stored securely in RabbitSign. Officium Connect only stores metadata 
                (document IDs, status, timestamps) - never the actual files. This keeps your 
                CRM lightweight and compliant.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RabbitSignIntegration;
